import { createStore } from '../core/state/store';
import { t } from './i18n/i18n';

/**
 * Background music for the landing page and auth screens.
 *
 * Separate from engine/audio.ts (the in-game theme) because landing and auth
 * run before the shell mounts — they need their own element, their own mute
 * toggle, and their own track. The in-game audio takes over once the shell
 * mounts and this one tears down.
 *
 * Same three browser-audio constraints as engine/audio.ts:
 *  1. Autoplay blocked until first gesture
 *  2. Preference persisted in localStorage (guarded)
 *  3. Single lazy element
 */

const STORAGE_KEY = 'dunes:landing-audio';
const TRACK_OGG = '/audio/landing-theme.ogg';
const TRACK_MP3 = '/audio/landing-theme.mp3';
const DEFAULT_VOLUME = 0.3;

export interface LandingAudioState {
  muted: boolean;
  playing: boolean;
  volume: number;
}

function readStored(): { muted: boolean; volume: number } {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { muted: false, volume: DEFAULT_VOLUME };
    const parsed = JSON.parse(raw) as Partial<LandingAudioState>;
    return {
      muted: parsed.muted === true,
      volume: typeof parsed.volume === 'number' ? Math.min(Math.max(parsed.volume, 0), 1) : DEFAULT_VOLUME,
    };
  } catch {
    return { muted: false, volume: DEFAULT_VOLUME };
  }
}

function persist(state: LandingAudioState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ muted: state.muted, volume: state.volume }));
  } catch {}
}

const stored = readStored();
export const landingAudioStore = createStore<LandingAudioState>({ ...stored, playing: false });

let element: HTMLAudioElement | null = null;
let armed = false;

function ensureElement(): HTMLAudioElement {
  if (element) return element;
  const audio = document.createElement('audio');
  audio.loop = true;
  audio.preload = 'none';
  audio.volume = landingAudioStore.get().volume;

  const ogg = document.createElement('source');
  ogg.src = TRACK_OGG;
  ogg.type = 'audio/ogg; codecs=opus';
  const mp3 = document.createElement('source');
  mp3.src = TRACK_MP3;
  mp3.type = 'audio/mpeg';
  audio.append(ogg, mp3);

  audio.addEventListener('error', () => landingAudioStore.set({ ...landingAudioStore.get(), playing: false }));
  audio.addEventListener('playing', () => landingAudioStore.set({ ...landingAudioStore.get(), playing: true }));
  audio.addEventListener('pause', () => landingAudioStore.set({ ...landingAudioStore.get(), playing: false }));

  element = audio;
  return audio;
}

async function startIfWanted(): Promise<void> {
  const state = landingAudioStore.get();
  if (state.muted || !armed) return;
  const audio = ensureElement();
  audio.volume = state.volume;
  try { await audio.play(); } catch {}
}

/**
 * Mount the landing audio system. Arms on first gesture, starts playing.
 * Returns a teardown that pauses and removes listeners.
 */
export function initLandingAudio(): () => void {
  const onFirstGesture = () => {
    armed = true;
    void startIfWanted();
  };
  const opts = { once: true, passive: true } as const;
  window.addEventListener('pointerdown', onFirstGesture, opts);
  window.addEventListener('keydown', onFirstGesture, opts);

  return () => {
    window.removeEventListener('pointerdown', onFirstGesture);
    window.removeEventListener('keydown', onFirstGesture);
    element?.pause();
    element = null;
    armed = false;
    landingAudioStore.set({ ...landingAudioStore.get(), playing: false });
  };
}

export function toggleLandingMuted(): void {
  const next = { ...landingAudioStore.get(), muted: !landingAudioStore.get().muted };
  landingAudioStore.set(next);
  persist(next);
  if (next.muted) element?.pause();
  else void startIfWanted();
}

/**
 * Renders a fixed-position mute toggle button. Returns { el, destroy }.
 * Append `el` to your container; call `destroy` on teardown.
 */
export function createMuteToggle(): { el: HTMLElement; destroy: () => void } {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'fixed bottom-4 right-4 z-50 min-w-11 min-h-11 px-3 flex items-center justify-center rounded bg-surface-2/80 border border-border text-text-dim hover:border-gold hover:text-gold transition-colors duration-[120ms] backdrop-blur touch-manipulation';
  btn.setAttribute('aria-label', t('auth.toggleMusic'));

  function paint(): void {
    const state = landingAudioStore.get();
    const sounding = !state.muted && state.playing;
    btn.innerHTML = sounding
      ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>'
      : '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="m16 9 5 6M21 9l-5 6"/></svg>';
    btn.classList.toggle('text-gold', sounding);
    btn.classList.toggle('text-text-dim', !sounding);
  }

  paint();
  const unsub = landingAudioStore.subscribe(paint);
  btn.addEventListener('click', toggleLandingMuted);

  return {
    el: btn,
    destroy() {
      unsub();
      btn.removeEventListener('click', toggleLandingMuted);
      btn.remove();
    },
  };
}
